import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { useAuth, apiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSelect, SelectAllCheckbox, BulkBar } from "@/components/BulkBar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Eye, KeyRound, UserCheck, UserX, Trash2, Shield, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
];

const DEPARTMENTS = [
  { value: "", label: "— None (Sales view)" },
  { value: "sales", label: "Sales" },
  { value: "logistics", label: "Logistics" },
  { value: "accounts", label: "Accounts" },
  { value: "service", label: "Service / Technician" },
  { value: "management", label: "Management" },
];
const MODULES = [
  { key: "rfqs", label: "RFQs" },
  { key: "orders", label: "Orders" },
  { key: "opportunities", label: "Opportunities" },
  { key: "service", label: "Service Center" },
  { key: "catalog", label: "Catalog & Inventory" },
  { key: "logistics", label: "Logistics" },
  { key: "crm", label: "CRM" },
  { key: "company", label: "Company Information" },
  { key: "finance", label: "Finance Hub" },
  { key: "team", label: "Team" },
  { key: "recycle_bin", label: "Recycle Bin" },
];

const EMPTY_FORM = { name: "", full_name: "", email: "", phone: "", job_title: "", department: "", role: "staff", permissions: null };

const ACTION_LABELS = {
  invite_user: "Invited member",
  change_role: "Changed role",
  activate: "Activated account",
  deactivate: "Deactivated account",
  remove_user: "Removed member",
  reset_password: "Sent password email",
  impersonate: "Accessed account",
};
const ACTION_COLORS = {
  invite_user: "bg-[#E6ECF5] text-[#1B4383]",
  change_role: "bg-[#E6ECF5] text-[#1B4383]",
  activate: "bg-emerald-100 text-emerald-700",
  deactivate: "bg-slate-100 text-slate-600",
  remove_user: "bg-[#F4E7E7] text-[#9D4848]",
  reset_password: "bg-[#F6EDD9] text-[#A87F31]",
  impersonate: "bg-amber-100 text-amber-800",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Team() {
  const { user, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("members");
  const [dlg, setDlg] = useState(null); // { mode: "add" | "edit", id?, role? }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const sel = useBulkSelect();
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectableIds = users.filter((u) => u.id !== user?.id).map((u) => u.id);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  const loadLogs = () => api.get("/audit-logs").then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "activity") loadLogs(); }, [tab]);

  const openAdd = () => { setForm({ ...EMPTY_FORM }); setDlg({ mode: "add" }); };
  const openEdit = (u) => {
    setForm({ name: u.name || "", full_name: u.full_name || u.name || "", email: u.email || "", phone: u.phone || "", job_title: u.job_title || "", department: u.department || "", role: u.role || "staff", permissions: u.permissions ?? null });
    setDlg({ mode: "edit", id: u.id, role: u.role });
  };
  const saveMember = async (sendInvite) => {
    setSaving(true);
    try {
      if (dlg.mode === "add") {
        await api.post("/users", { ...form, send_invite: sendInvite });
        toast.success(sendInvite ? "Member created & invite sent" : "Member created");
      } else {
        await api.put(`/users/${dlg.id}`, form);
        toast.success("Details updated");
      }
      setDlg(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const setRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setActive = async (u, is_active) => {
    try { await api.put(`/users/${u.id}`, { is_active }); toast.success(is_active ? "Activated" : "Deactivated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPw = async (u) => {
    try { await api.post(`/users/${u.id}/reset-password`); toast.success("Set-password email sent"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const bulkDelete = async () => {
    const ids = [...sel.selected];
    if (!window.confirm(`Remove ${ids.length} member${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      if (data.skipped && data.skipped.length) toast.warning(data.skipped[0].reason);
      toast.success(`Removed ${data.deleted} member${data.deleted !== 1 ? "s" : ""}`);
      sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };
  const access = async (u) => {
    try { await impersonate(u.id); } catch (e) { toast.error(apiError(e)); }
  };

  const RoleBadge = ({ role }) => (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
      role === "admin" ? "bg-[#E6ECF5] text-[#1B4383] border-[#C9D8EC]"
        : role === "logistics" ? "bg-[#FBF3E4] text-[#A87F31] border-[#EAD9B4]"
        : "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {role === "admin" && <Shield className="h-3 w-3" />}
      {role === "admin" ? "Admin" : role === "logistics" ? "Logistics" : "Staff"}
    </span>
  );

  const UserActions = ({ u }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" data-testid={`user-menu-${u.id}`}><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="mr-2 h-4 w-4" /> Edit Details</DropdownMenuItem>
        {u.id !== user?.id && (
          <DropdownMenuItem onClick={() => access(u)} data-testid={`impersonate-${u.id}`}><Eye className="mr-2 h-4 w-4" /> Access Account</DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => setRole(u, u.role === "admin" ? "staff" : "admin")} data-testid={`toggle-role-${u.id}`}><Shield className="mr-2 h-4 w-4" /> Make {u.role === "admin" ? "Staff" : "Admin"}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => resetPw(u)} data-testid={`reset-pw-${u.id}`}><KeyRound className="mr-2 h-4 w-4" /> {u.pending ? "Resend Invite" : "Send Reset Email"}</DropdownMenuItem>
        {u.is_active ? (
          <DropdownMenuItem onClick={() => setActive(u, false)} data-testid={`deactivate-${u.id}`}><UserX className="mr-2 h-4 w-4" /> Deactivate</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => setActive(u, true)} data-testid={`activate-${u.id}`}><UserCheck className="mr-2 h-4 w-4" /> Activate</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => remove(u)} className="text-destructive focus:text-destructive" data-testid={`remove-${u.id}`}><Trash2 className="mr-2 h-4 w-4" /> Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const StatusChip = ({ u }) => (
    !u.is_active ? <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">Inactive</span>
      : u.pending ? <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Invite pending</span>
        : <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Active</span>
  );

  const AccessChip = ({ u }) => (
    u.role === "admin" || u.permissions == null
      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Full</span>
      : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{u.permissions.length} section{u.permissions.length !== 1 ? "s" : ""}</span>
  );

  return (
    <AppLayout title="Team" actions={
      tab === "members" ? <Button onClick={openAdd} data-testid="invite-user-button"><Plus className="mr-2 h-4 w-4" />Add Member</Button> : null
    }>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4" data-testid="team-tabs">
          <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
      <div className="hidden rounded-lg border border-border bg-white overflow-x-auto sm:block">
        <table className="data-table w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
              <th className="w-10 px-5 py-3"><SelectAllCheckbox ids={selectableIds} selected={sel.selected} onToggleAll={sel.toggleAll} /></th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Job Title</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Phone</th>
              <th className="px-5 py-3 font-medium text-center">Role</th>
              <th className="px-5 py-3 font-medium text-center">Access</th>
              <th className="px-5 py-3 font-medium text-center">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} onClick={() => u.id !== user?.id && sel.toggle(u.id)} className={`cursor-pointer border-t border-border hover:bg-slate-50 ${sel.isSelected(u.id) ? "bg-[#EEF3FA]" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={sel.isSelected(u.id)} disabled={u.id === user?.id} onCheckedChange={() => sel.toggle(u.id)} aria-label="Select member" data-testid={`bulk-select-user-${u.id}`} />
                </td>
                <td className="px-5 py-3 font-medium text-slate-900">
                  {u.full_name || u.name || "—"} {u.id === user?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                </td>
                <td className="px-5 py-3 text-muted-foreground">{u.job_title || "—"}</td>
                <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-5 py-3 text-muted-foreground">{u.phone || "—"}</td>
                <td className="px-5 py-3 text-center"><RoleBadge role={u.role} /></td>
                <td className="px-5 py-3 text-center" data-testid={`user-access-${u.id}`}>
                  {u.role === "admin" || u.permissions == null
                    ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Full</span>
                    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{u.permissions.length} section{u.permissions.length !== 1 ? "s" : ""}</span>}
                </td>
                <td className="px-5 py-3 text-center">
                  {!u.is_active ? (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">Inactive</span>
                  ) : u.pending ? (
                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Invite pending</span>
                  ) : (
                    <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Active</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}><UserActions u={u} /></td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={9} className="px-5 py-10 text-center text-muted-foreground">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="space-y-2.5 sm:hidden" data-testid="team-members-cards">
        {users.map((u) => (
          <div key={u.id} onClick={() => u.id !== user?.id && sel.toggle(u.id)} className={`cursor-pointer rounded-lg border border-border p-3.5 ${sel.isSelected(u.id) ? "bg-[#EEF3FA]" : "bg-white"}`} data-testid={`user-card-${u.id}`}>
            <div className="flex items-start gap-3">
              <span onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.isSelected(u.id)} disabled={u.id === user?.id} onCheckedChange={() => sel.toggle(u.id)} aria-label="Select member" className="mt-1" data-testid={`bulk-select-user-card-${u.id}`} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-slate-900">{u.full_name || u.name || "—"}{u.id === user?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</span>
                  <span onClick={(e) => e.stopPropagation()}><UserActions u={u} /></span>
                </div>
                {u.job_title && <div className="truncate text-xs text-muted-foreground">{u.job_title}</div>}
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                {u.phone && <div className="text-xs text-muted-foreground">{u.phone}</div>}
                <div className="mt-2 flex flex-wrap items-center gap-1.5"><RoleBadge role={u.role} /><AccessChip u={u} /><StatusChip u={u} /></div>
              </div>
            </div>
          </div>
        ))}
        {!users.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No team members yet.</div>}
      </div>

      {tab === "members" && (
        <BulkBar count={sel.selected.size} onClear={sel.clear} onDelete={bulkDelete} canDelete deleting={bulkBusy} deleteLabel="Remove" />
      )}
        </TabsContent>

        <TabsContent value="activity">
          <div className="hidden rounded-lg border border-border bg-white overflow-x-auto sm:block">
            <table className="data-table w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-5 py-3 font-medium">Action</th>
                  <th className="px-5 py-3 font-medium">By</th>
                  <th className="px-5 py-3 font-medium">Target</th>
                  <th className="px-5 py-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-border hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                    <td className="px-5 py-3 font-mono text-xs whitespace-nowrap text-muted-foreground">{fmtTime(l.at)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-600"}`}>
                        {ACTION_LABELS[l.action] || l.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-900">{l.admin_name || l.admin_email || "—"}</td>
                    <td className="px-5 py-3 text-muted-foreground">{l.target_name || l.target_email || "—"}</td>
                    <td className="px-5 py-3 text-muted-foreground">{l.detail || "—"}</td>
                  </tr>
                ))}
                {!logs.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No activity recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="space-y-2.5 sm:hidden" data-testid="team-activity-cards">
            {logs.map((l) => (
              <div key={l.id} className="rounded-lg border border-border bg-white p-3.5" data-testid={`audit-card-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-600"}`}>{ACTION_LABELS[l.action] || l.action}</span>
                  <span className="text-[10px] text-muted-foreground">{fmtTime(l.at)}</span>
                </div>
                <div className="mt-1.5 text-xs text-slate-700">{l.admin_name || l.admin_email || "—"}{(l.target_name || l.target_email) ? ` → ${l.target_name || l.target_email}` : ""}</div>
                {l.detail && <div className="text-xs text-muted-foreground">{l.detail}</div>}
              </div>
            ))}
            {!logs.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No activity recorded yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader><DialogTitle>{dlg?.mode === "add" ? "Add Member" : "Edit Member Details"}</DialogTitle></DialogHeader>
          {dlg?.mode === "add" && (
            <DialogDescription className="-mt-2">Create the member's profile now. Save without inviting, or save and email them a set-password link.</DialogDescription>
          )}
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ronick" data-testid="member-name" />
            </div>
            <div className="space-y-2">
              <Label>Full Name <span className="font-normal text-muted-foreground">(used in email signatures)</span></Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="e.g. Ronick Somaiya" data-testid="member-fullname" />
            </div>
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="e.g. Sales Engineer" data-testid="member-job-title" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Department <span className="font-normal text-muted-foreground">(dashboard view)</span></Label>
                <Select value={form.department || "none"} onValueChange={(v) => setForm({ ...form, department: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="member-department"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d.value || "none"} value={d.value || "none"}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email Address {dlg?.mode === "edit" && <span className="font-normal text-muted-foreground">(admin can amend)</span>}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. +255 xxx xxx xxx" data-testid="member-phone" />
            </div>
            <div className="space-y-2">
              <Label>Section Access</Label>
              {form.role === "admin" ? (
                <p className="text-xs text-muted-foreground">Admins have full access to every section.</p>
              ) : (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <label className="flex items-center gap-2 text-sm font-medium" data-testid="member-full-access">
                    <Checkbox checked={form.permissions === null} onCheckedChange={(c) => setForm((f) => ({ ...f, permissions: c ? null : [] }))} />
                    Full Access (All Sections)
                  </label>
                  {form.permissions !== null && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {MODULES.map((m) => (
                        <label key={m.key} className="flex items-center gap-2 text-sm" data-testid={`member-perm-${m.key}`}>
                          <Checkbox checked={form.permissions.includes(m.key)} onCheckedChange={(c) => setForm((f) => ({ ...f, permissions: c ? [...f.permissions, m.key] : f.permissions.filter((x) => x !== m.key) }))} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 border-t border-border bg-background px-6 py-3">
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            {dlg?.mode === "add" ? (
              <>
                <Button variant="outline" onClick={() => saveMember(false)} disabled={!form.name || !form.email || saving} data-testid="member-save-only">Save only</Button>
                <Button onClick={() => saveMember(true)} disabled={!form.name || !form.email || saving} data-testid="member-save-invite">Save &amp; Invite</Button>
              </>
            ) : (
              <Button onClick={() => saveMember(false)} disabled={!form.name || !form.email || saving} data-testid="edit-user-save">Save</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
